import React, { useState, useEffect } from "react";
import { Plus, SquareChartGantt, Ticket, MessageSquare } from "lucide-react";
import { FormattedMessage, injectIntl } from "react-intl";
import { Box, Paper, Button, Accordion, AccordionSummary, AccordionDetails, Typography, Grid, Chip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import AutomationCard from "../../components/automation/automation-card";
import ActionMenu from "../../components/automation/action-menu";
import SearchBar from "../../components/automation/search-bar";
import SimpleAutoComplete from "../../components/auto-complete/autocomplete";
import styles from "../../styles/automation-styles";

// F5.10 — Card-selector profissional pros 2 targets (Chamados | Conversas).
// Pattern inspirado em segmented selector de Zendesk/Freshchat: cards side-by-side com
// icone grande, titulo, subtitle e badge de counter. Visual states:
//   - inativo: border 1px cinza claro, fundo branco, texto neutro
//   - ativo:   border-left 4px verde Mexx (#8BC34A), fundo verde pastel, texto bold
//   - hover (inativo): background cinza clarinho + border mais escuro
const TargetSelectorCard = ({ icon, title, subtitle, count, active, onClick, disabled }) => {
  const [hover, setHover] = React.useState(false);
  const base = {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "18px 20px",
    borderRadius: 12,
    cursor: disabled ? "default" : "pointer",
    transition: "all 0.18s ease",
    userSelect: "none",
    opacity: disabled && !active ? 0.5 : 1,
  };
  const activeStyle = {
    border: "1px solid #8BC34A",
    borderLeft: "4px solid #8BC34A",
    backgroundColor: "#f3f8ec",
    boxShadow: "0 1px 3px rgba(139,195,74,0.15)",
  };
  const inactiveStyle = {
    border: "1px solid #e0e0e0",
    borderLeft: "4px solid #e0e0e0",
    backgroundColor: hover && !disabled ? "#fafafa" : "#ffffff",
  };
  return (
    <div
      style={{ ...base, ...(active ? activeStyle : inactiveStyle) }}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      aria-pressed={active}
      aria-disabled={disabled}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 10, display: "flex",
        alignItems: "center", justifyContent: "center",
        backgroundColor: active ? "#8BC34A" : "#f5f5f5",
        color: active ? "#ffffff" : "#616161",
        transition: "all 0.18s ease",
      }}>{React.cloneElement(icon, { color: active ? "#ffffff" : "#616161" })}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 15, fontWeight: active ? 700 : 600,
          color: active ? "#2e7d32" : "#212121", lineHeight: 1.2,
        }}>{title}</div>
        <div style={{ fontSize: 12, color: "#757575", marginTop: 2 }}>{subtitle}</div>
      </div>
      {typeof count === "number" && (
        <div style={{
          minWidth: 32, height: 24, padding: "0 8px", borderRadius: 12,
          backgroundColor: active ? "#8BC34A" : "#eeeeee",
          color: active ? "#ffffff" : "#616161",
          fontSize: 13, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.18s ease",
        }}>{count}</div>
      )}
    </div>
  );
};

// Main AutomationList Component
const AutomationList = injectIntl((props) => {
  const intl = props.intl;
  // F5.10 — Descriptions por (target, type) via i18n. Chaves em automation.list.desc.*
  const descriptionsCalled = [
    intl.formatMessage({ id: "automation.list.desc.called.1" }),
    intl.formatMessage({ id: "automation.list.desc.called.2" }),
    intl.formatMessage({ id: "automation.list.desc.called.3" }),
  ];
  const descriptionsChat = [
    intl.formatMessage({ id: "automation.list.desc.chat.1" }),
    intl.formatMessage({ id: "automation.list.desc.chat.2" }),
    intl.formatMessage({ id: "automation.list.desc.chat.3" }),
  ];
  // F5.10 — target top-level (called | chat). Default do localStorage, fallback 'called'.
  const [activeTarget, setActiveTarget] = useState(localStorage.getItem("activeAutomationTarget") || "called");
  const [activeTab, setActiveTab] = useState(parseInt(localStorage.getItem("activeAutomationTab")) || 0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleTargetChange = (_e, value) => {
    if (!value) return; // ToggleButtonGroup emite null se desselecionar — manter atual
    setActiveTarget(value);
    localStorage.setItem("activeAutomationTarget", value);
  };

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
    localStorage.setItem("activeAutomationTab", tabIndex);
  };

  const handleMenuOpen = (e, automation) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      x: rect.left + window.scrollX - 150,
      y: rect.bottom + window.scrollY - 25,
    });
    setSelectedAutomation(automation);
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  // F5.10 — data chega como { called: [[], [], []], chat: [[], [], []] }.
  // Fallback pra retrocompat: array simples (3 pos) tratado como Called.
  const dataBuckets = Array.isArray(props.data)
    ? { called: props.data, chat: [[], [], []] }
    : props.data || { called: [[], [], []], chat: [[], [], []] };

  const currentBucket = dataBuckets[activeTarget] || [[], [], []];

  const automations = (currentBucket[activeTab] || []).filter((automation) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      automation.title.toLowerCase().includes(searchLower) ||
      automation.condition.toLowerCase().includes(searchLower)
    );
  });

  const descriptions = activeTarget === "chat" ? descriptionsChat : descriptionsCalled;

  useEffect(() => {
    const handleClickOutside = () => {
      setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpen]);

  // F5.10 — nova regra passa &target=chat quando operador escolheu chat.
  const newRuleHref = `/${props.match.params.lang}/automation-registration?type=${activeTab + 1}${activeTarget === "chat" ? "&target=chat" : ""}`;

  const tabLabels = {
    called: [
      intl.formatMessage({ id: "automation.list.tab.called.creation" }),
      intl.formatMessage({ id: "automation.list.tab.called.update" }),
      intl.formatMessage({ id: "automation.list.tab.called.hourly" }),
    ],
    chat: [
      intl.formatMessage({ id: "automation.list.tab.chat.creation" }),
      intl.formatMessage({ id: "automation.list.tab.chat.update" }),
      intl.formatMessage({ id: "automation.list.tab.chat.hourly" }),
    ],
  };

  return (
    <div style={styles.contentWrapper}>
      <h1 style={styles.heading}>{intl.formatMessage({ id: "automation.list.title" })}</h1>

      {/* F5.10 — Target selector profissional: 2 cards side-by-side, visual pattern Zendesk/Freshchat. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        <TargetSelectorCard
          icon={<Ticket size={22} />}
          title={intl.formatMessage({ id: "automation.target_entity.called" })}
          subtitle={intl.formatMessage({ id: "automation.list.desc.target.called" })}
          count={(dataBuckets.called || []).reduce((s, arr) => s + (arr?.length || 0), 0)}
          active={activeTarget === "called"}
          onClick={() => handleTargetChange(null, "called")}
        />
        <TargetSelectorCard
          icon={<MessageSquare size={22} />}
          title={intl.formatMessage({ id: "automation.target_entity.chat" })}
          subtitle={intl.formatMessage({ id: "automation.list.desc.target.chat" })}
          count={(dataBuckets.chat || []).reduce((s, arr) => s + (arr?.length || 0), 0)}
          active={activeTarget === "chat"}
          onClick={() => handleTargetChange(null, "chat")}
        />
      </Box>

      <div style={styles.tabContainer}>
        <div style={styles.tabRow}>
          <span style={styles.tabLabel}>{intl.formatMessage({ id: "automation.list.rules_executed_on" })}</span>
          <div style={styles.tabsWrapper}>
            {[0, 1, 2].map((idx) => (
              <button
                key={idx}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === idx ? styles.activeTabButton : {}),
                }}
                onClick={() => handleTabClick(idx)}
              >
                {tabLabels[activeTarget][idx]}
                <span style={styles.tabCount}>{currentBucket[idx]?.length ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
        {/*<SearchBar onSearch={setSearchTerm} />*/}
      </div>

      <p style={styles.infoText}>
        {descriptions[activeTab] ?? descriptions[0]}
      </p>

      <div style={styles.actionsRow}>
        <Button
          style={styles.newRuleButton}
          component={Link}
          {...{ to: newRuleHref }}
        >
          <Plus size={18} />
          <span>{intl.formatMessage({ id: "automation.list.new_rule" })}</span>
        </Button>

        <Button
          style={styles.newRuleButton}
          component={Link}
          {...{
            to: `/${props.match.params.lang}/automation-templates`,
          }}
        >
          <SquareChartGantt size={18} />
          <span>{intl.formatMessage({ id: "automation.list.templates" })}</span>
        </Button>
      </div>

      {/* Filtros — mesmo padrao Accordion do report-list (chave i18n "filters" ja existente) */}
      {props.activeFilterOptions && (
        <div style={{ marginBottom: 12 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="automation-list-filters" id="automation-list-filters-header">
              <Typography style={{ fontWeight: 600 }}>
                <FormattedMessage id="filters" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <SimpleAutoComplete
                    label={intl.formatMessage({ id: "status" })}
                    options={props.activeFilterOptions}
                    stateName="activeFilter"
                    changeSelect={props.changeActiveFilter}
                    selected={props.activeFilter}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </div>
      )}

      {automations.length > 0 ? (
        automations.map((automation) => (
          <AutomationCard
            key={automation.id}
            automation={automation}
            onMenuOpen={handleMenuOpen}
            updateActiveAsync={props.updateActiveAsync}
            getItemForUpdateAsync={props.getItemForUpdateAsync}
          />
        ))
      ) : (
        <div style={styles.emptyContainer}>
          <p style={styles.emptyText}>
            {searchTerm
              ? intl.formatMessage({ id: "automation.list.empty.search" })
              : (activeTarget === "chat"
                  ? intl.formatMessage({ id: "automation.list.empty.chat" })
                  : intl.formatMessage({ id: "automation.list.empty.called" }))
            }
          </p>
        </div>
      )}

      <ActionMenu
        isOpen={menuOpen}
        position={menuPosition}
        onClose={handleMenuClose}
        selectedAutomation={selectedAutomation}
        deleteAsync={props.deleteAsync}
        cloneItemAsync={props.cloneItemAsync}
        getItemForUpdateAsync={props.getItemForUpdateAsync}
        history={props.history}
        lang={props.match && props.match.params && props.match.params.lang}
      />
    </div>
  );
});
export default AutomationList;
